import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { getAvatarBg, getContactInitials } from '../../utils/chatAvatars'
import { rankByFuzzySearch } from '../../utils/fuzzySearch'

const ROLE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'GP', label: 'GP' },
  { id: 'LP', label: 'LP' },
  { id: 'Startup', label: 'Startup' },
  { id: 'ServiceProvider', label: 'Service' },
]

const SEARCH_FIELDS = ['name', 'company', 'role', 'email']

function roleLabel(role) {
  if (role === 'ServiceProvider') return 'Service'
  return role || ''
}

function recencyValue(candidate) {
  if (!candidate.lastChatAt) return 0
  const ts = new Date(candidate.lastChatAt).getTime()
  return Number.isNaN(ts) ? 0 : ts
}

function filterByRole(candidates, roleFilter) {
  if (!roleFilter || roleFilter === 'all') return candidates
  return candidates.filter((c) => c.role === roleFilter)
}

function splitRecentAndOthers(candidates) {
  const recent = []
  const others = []
  for (const candidate of candidates) {
    if (candidate.lastChatAt) recent.push(candidate)
    else others.push(candidate)
  }
  return { recent, others }
}

function CandidateRow({ candidate, checked, onToggle }) {
  const id = String(candidate.id)
  return (
    <label className={`admin-share-recipient-row${checked ? ' selected' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onToggle(candidate, e.target.checked)}
      />
      <span
        className="admin-share-recipient-avatar"
        style={{ background: getAvatarBg(candidate) }}
      >
        {getContactInitials(candidate)}
      </span>
      <span className="admin-share-recipient-main">
        <span className="admin-share-recipient-name">
          {candidate.name}
          {candidate.lastChatAt ? <span className="admin-share-recipient-recent">Recent</span> : null}
        </span>
        <span className="admin-share-recipient-meta">
          {[candidate.company, roleLabel(candidate.role), candidate.email].filter(Boolean).join(' · ')}
        </span>
      </span>
    </label>
  )
}

export default function ShareRecipientPicker({ selected, onChange }) {
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    axios.get('/api/share-packages/recipient-candidates')
      .then((res) => {
        if (!cancelled) setCandidates(res.data?.candidates || [])
      })
      .catch(() => {
        if (!cancelled) setCandidates([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const selectedIds = useMemo(
    () => new Set(selected.map((item) => String(item.id))),
    [selected]
  )

  const roleScoped = useMemo(
    () => filterByRole(candidates, roleFilter),
    [candidates, roleFilter]
  )

  const filtered = useMemo(() => {
    const trimmed = search.trim()
    if (!trimmed) return roleScoped
    return rankByFuzzySearch(roleScoped, trimmed, {
      fields: SEARCH_FIELDS,
      getRecency: recencyValue,
    })
  }, [roleScoped, search])

  const showGrouped = !search.trim()

  const { recent, others } = useMemo(() => {
    if (!showGrouped) return { recent: [], others: filtered }
    return splitRecentAndOthers(filtered)
  }, [filtered, showGrouped])

  const toggle = (candidate, checked) => {
    const id = String(candidate.id)
    if (checked) {
      if (selectedIds.has(id)) return
      onChange([...selected, candidate])
      return
    }
    onChange(selected.filter((item) => String(item.id) !== id))
  }

  const removeChip = (id) => {
    onChange(selected.filter((item) => String(item.id) !== String(id)))
  }

  const renderList = (list) => list.map((candidate) => (
    <CandidateRow
      key={candidate.id}
      candidate={candidate}
      checked={selectedIds.has(String(candidate.id))}
      onToggle={toggle}
    />
  ))

  return (
    <div className="admin-share-recipient-picker">
      <div className="admin-share-recipient-toolbar">
        <input
          className="admin-field-input"
          type="search"
          placeholder="Fuzzy search: name, company, role, email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="admin-share-recipient-count">
          {selected.length} selected
        </span>
      </div>

      <div className="admin-share-recipient-role-filters">
        {ROLE_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`admin-share-recipient-role-btn${roleFilter === filter.id ? ' active' : ''}`}
            onClick={() => setRoleFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {selected.length > 0 && (
        <div className="admin-share-recipient-chips">
          {selected.map((item) => (
            <span key={item.id} className="admin-share-recipient-chip">
              <span>{item.name}</span>
              <button type="button" aria-label={`Remove ${item.name}`} onClick={() => removeChip(item.id)}>×</button>
            </span>
          ))}
        </div>
      )}

      <div className="admin-share-recipient-list">
        {loading ? (
          <p className="admin-share-recipient-empty">Loading members…</p>
        ) : filtered.length === 0 ? (
          <p className="admin-share-recipient-empty">No matching members</p>
        ) : showGrouped ? (
          <>
            {recent.length > 0 && (
              <div className="admin-share-recipient-section">
                <div className="admin-share-recipient-section-title">Recent chats</div>
                {renderList(recent)}
              </div>
            )}
            {others.length > 0 && (
              <div className="admin-share-recipient-section">
                {recent.length > 0 && (
                  <div className="admin-share-recipient-section-title">All members</div>
                )}
                {renderList(others)}
              </div>
            )}
          </>
        ) : renderList(filtered)}
      </div>
    </div>
  )
}
